import {
  Box,
  Flex,
  Heading,
  Image,
  Text,
  Stack,
  Container,
  SimpleGrid,
  IconButton,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

import Navbar from "./Navbar";
import Footer from "./Footer";
import { useAuth } from './AuthContext';

import HeroImage from "./images/stamps.png";
import PocBadge from "./images/poc.png";
import StudioImage from "./images/studio1.png";
import CodifyImage from "./images/codify1.png";
import PannerImage from "./images/panner1.png";
import OraclesImage from "./images/oracles1.png";
import ContractOSImage from "./images/contractos1.png";
import RegistersImage from "./images/registers1.png";

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const products = [
    {
      name: "Proof of Concept",
      tagline: "Explore the mrc.ai PoC",
      description: "This Proof of Concept demonstrates how a smart contracting platform can centralize co-authored contracts for standardized (Re)insurance Products — from Submission through Bind and into full policy lifecycle management — within a single, unified interface supporting multiple Products and participants.",
      image: PocBadge,
      path: "/Poc"
    },
    {
      name: "Studio",
      tagline: "Data Generative Contracts",
      description: "Write free flowing contracts with realtime co-authoring, controlled access to headings and the Ruschlikon standard electronic placing process performed without leaving the document.",
      image: StudioImage,
      path: "/studio-info"
    },
    {
      name: "Codify",
      tagline: "Configurable Data Extraction",
      description: "Convert text & documents into data. Fast development of extraction rules using any data standard in any format.",
      image: CodifyImage,
      path: "/codify-info"
    },
    {
      name: "Panner",
      tagline: "Placement to Policy",
      description: "Manage Placement to Accounting allocation with schema based premium distribution rules and straight through claims processing.",
      image: PannerImage,
      path: "/panner-info"
    },
    {
      name: "Oracles",
      tagline: "Centralised Agreement",
      description: "Centralised claims agreement and contract decisioning, enabling an AI augmented future.",
      image: OraclesImage,
      path: "/oracles-info"
    },
    {
      name: "ContractOS",
      tagline: "Event Driven Processing",
      description: "Contracts operate themselves with product specific automation based on a signals, trigger and events data pipeline with peer to peer net settlement.",
      image: ContractOSImage,
      path: "/contractos-info"
    },
    {
      name: "Registers",
      tagline: "Asset Register Management",
      description: "Define and manage asset registers, attach assets to contracts, and subscribe registers or assets to API based signals.",
      image: RegistersImage,
      path: "/registers-info"
    }
  ];

  const handleClick = () => {
    navigate("/Poc");
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % products.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + products.length) % products.length);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 5000); // Auto-scroll every 5 seconds

    return () => clearInterval(timer);
  }, [currentSlide]);

  return (
    <Box>
      <Navbar />

      {/* Background image */}
      <Image
        src={HeroImage}
        alt="Hero Watermark"
        width="100%"
        height="100%"
        objectFit="cover"
        opacity={0.30}
        position="absolute"
        top={0}
        left={0}
        zIndex={-1}
        filter="blur(1px)"
      />

      {!user ? (
        <Container maxW="container.md" py={20}>
          <Box
            bg="white"
            p={6}
            borderRadius="md"
            boxShadow="md"
            textAlign="center"
          >
            <Heading size="lg" mb={4}>
              Welcome to mrc.ai
            </Heading>
            <Text fontSize="md">
              You must be registered and logged in to view this content.
            </Text>
          </Box>
        </Container>
      ) : (
        <Container maxW="container.xl" py={8}>
          <Stack spacing={8} align="stretch">
            {/* Hero Section */}
            <Box
              bg="rgba(255,255,255,0.95)"
              p={12}
              borderRadius="lg"
              boxShadow="xl"
              textAlign="center"
            >
              <Heading
                fontSize="6xl"
                mb={4}
                color="gray.800"
                fontWeight="bold"
              >
                Modern (Re)insurance Contracts
              </Heading>
              <Text fontSize="xl" color="gray.700" mb={8} lineHeight="1.8">
                Transforming the insurance industry with intelligent contract automation
              </Text>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mt={8}>
                <Box p={6} bg="blue.50" borderRadius="md" boxShadow="md">
                  <Heading size="md" color="gray.800" mb={3}>
                    UNIQUE
                  </Heading>
                  <Text color="gray.700">
                    A new concept in (Re)insurance Products - A Market Workbench
                  </Text>
                </Box>

                <Box p={6} bg="blue.50" borderRadius="md" boxShadow="md">
                  <Heading size="md" color="gray.800" mb={3}>
                    FIRST TO MARKET
                  </Heading>
                  <Text color="gray.700">
                    Pioneering beautifully designed platform that's stylish and functional, provides value now and opens the door to a Smart Contracting future
                  </Text>
                </Box>

                <Box p={6} bg="blue.50" borderRadius="md" boxShadow="md">
                  <Heading size="md" color="gray.800" mb={3}>
                    AUTHENTIC
                  </Heading>
                  <Text color="gray.700">
                    Built by industry experts for industry professionals
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* Product Carousel */}
            <Box
              bg="rgba(255,255,255,0.95)"
              borderRadius="lg"
              boxShadow="xl"
              position="relative"
              overflow="hidden"
            >
              <Flex
                align="center"
                justify="space-between"
                p={8}
                cursor="pointer"
                onClick={() => handleNavigation(products[currentSlide].path)}
                _hover={{ bg: "rgba(245,245,255,0.98)" }}
                transition="background 0.3s ease"
                minH="300px"
              >
                {/* Left Navigation Button */}
                <IconButton
                  icon={<ChevronLeftIcon boxSize={8} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    prevSlide();
                  }}
                  position="absolute"
                  left={4}
                  zIndex={2}
                  bg="rgba(255,255,255,0.7)"
                  color="gray.600"
                  variant="ghost"
                  size="lg"
                  borderRadius="full"
                  aria-label="Previous product"
                  _hover={{
                    bg: "rgba(255,255,255,0.9)",
                    color: "gray.800",
                    transform: "scale(1.1)"
                  }}
                  boxShadow="sm"
                />

                {/* Image Section */}
                <Box
                  flex="0 0 40%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pr={6}
                >
                  <Image
                    src={products[currentSlide].image}
                    alt={products[currentSlide].name}
                    maxW="100%"
                    maxH="250px"
                    objectFit="contain"
                    transition="all 0.5s ease"
                  />
                </Box>

                {/* Text Section */}
                <Box flex="1" pl={6}>
                  <Text fontSize="lg" color="gray.700" lineHeight="1.8">
                    {products[currentSlide].description}
                  </Text>
                  <Text fontSize="sm" color="blue.500" mt={4} fontWeight="medium">
                    Click to learn more →
                  </Text>
                </Box>

                {/* Right Navigation Button */}
                <IconButton
                  icon={<ChevronRightIcon boxSize={8} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    nextSlide();
                  }}
                  position="absolute"
                  right={4}
                  zIndex={2}
                  bg="rgba(255,255,255,0.7)"
                  color="gray.600"
                  variant="ghost"
                  size="lg"
                  borderRadius="full"
                  aria-label="Next product"
                  _hover={{
                    bg: "rgba(255,255,255,0.9)",
                    color: "gray.800",
                    transform: "scale(1.1)"
                  }}
                  boxShadow="sm"
                />
              </Flex>

              {/* Slide Indicators */}
              <Flex
                justify="center"
                pb={4}
                gap={2}
              >
                {products.map((_, index) => (
                  <Box
                    key={index}
                    w={currentSlide === index ? "32px" : "8px"}
                    h="8px"
                    borderRadius="full"
                    bg={currentSlide === index ? "blue.500" : "gray.300"}
                    cursor="pointer"
                    onClick={() => setCurrentSlide(index)}
                    transition="all 0.3s ease"
                    _hover={{ bg: currentSlide === index ? "blue.600" : "gray.400" }}
                  />
                ))}
              </Flex>
            </Box>
          </Stack>
        </Container>
      )}
      <Footer />
    </Box>
  );
};

export default Home;
